import React, { ChangeEvent, useState } from 'react';

import { Input, Modal, toastService } from '@thefarmersfront/kitchen';

import { FormField } from '~/components/common';

import { useLoadingStore } from '~/store';
import {
  validateEmailFormat,
  validateNumberAndHyphenOnly,
  validateTelFormat,
  validateVenderManagerName,
} from '~/utils';

import { DEFAULT_INPUT_STATUS, TOAST_MESSAGE } from '~/constants';

import type { TypeErrorResponseData, TypeInputState } from '~/types/common.types';
import type { TypeOwnerSupplierManager } from '~/types/vender.types';

import styles from './VenderSupplierManagerAddModal.module.scss';

type TypeVenderSupplierManagerAddModalProps = {
  open: boolean;
  onCancel: () => void;
  onConfirm: (managerInfo: TypeOwnerSupplierManager) => void | Promise<void>;
};

const DEFAULT_MANAGER_ADD_FORM = {
  name: '',
  position: '',
  tel: '',
  email: '',
};

/**
 * [화주사 정보 관리 > 공급사 관리] 담당자 추가 모달
 */
const VenderSupplierManagerAddModal = ({ open, onCancel, onConfirm }: TypeVenderSupplierManagerAddModalProps) => {
  const { setIsLoading } = useLoadingStore();

  const [managerAddForm, setManagerAddForm] = useState(DEFAULT_MANAGER_ADD_FORM);

  const [nameStatus, setNameStatus] = useState<TypeInputState>(DEFAULT_INPUT_STATUS);
  const [telStatus, setTelStatus] = useState<TypeInputState>(DEFAULT_INPUT_STATUS);
  const [emailStatus, setEmailStatus] = useState<TypeInputState>(DEFAULT_INPUT_STATUS);

  const resetForm = () => {
    setManagerAddForm(DEFAULT_MANAGER_ADD_FORM);

    setNameStatus(DEFAULT_INPUT_STATUS);
    setTelStatus(DEFAULT_INPUT_STATUS);
    setEmailStatus(DEFAULT_INPUT_STATUS);
  };

  const handleFocusName = () => setNameStatus(DEFAULT_INPUT_STATUS);

  const handleFocusTel = () => setTelStatus(DEFAULT_INPUT_STATUS);

  const handleFocusEmail = () => setEmailStatus(DEFAULT_INPUT_STATUS);

  const handleChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valueWithoutSpaces = e.target.value.replace(/\s+/g, '');

    setManagerAddForm({ ...managerAddForm, name: valueWithoutSpaces });
  };

  const handleChangePosition = (e: ChangeEvent<HTMLInputElement>) =>
    setManagerAddForm({ ...managerAddForm, position: e.target.value });

  const handleChangeTel = (e: ChangeEvent<HTMLInputElement>) => {
    const updatedValue = e.target.value;

    if (validateNumberAndHyphenOnly(updatedValue)) {
      setManagerAddForm({ ...managerAddForm, tel: updatedValue });
    }
  };

  const handleChangeEmail = (e: ChangeEvent<HTMLInputElement>) =>
    setManagerAddForm({ ...managerAddForm, email: e.target.value });

  const handleCancel = () => {
    resetForm();
    onCancel();
  };

  const validateForm = () => {
    let isValidForm = true;

    // 담당자명 유효성 체크
    if (managerAddForm.name === '') {
      setNameStatus({ status: 'error', message: '필수 입력입니다.' });
      isValidForm = false;
    } else if (!validateVenderManagerName(managerAddForm.name)) {
      setNameStatus({ status: 'error', message: '올바른 형식으로 입력해주세요.' });
      isValidForm = false;
    }

    // 휴대폰번호 유효성 체크
    if (managerAddForm.tel === '') {
      setTelStatus({ status: 'error', message: '필수 입력입니다.' });
      isValidForm = false;
    } else if (!validateTelFormat(managerAddForm.tel)) {
      setTelStatus({ status: 'error', message: '올바른 형식으로 입력해주세요.' });
      isValidForm = false;
    }

    // 이메일 유효성 체크
    if (managerAddForm.email.length > 0 && !validateEmailFormat(managerAddForm.email)) {
      setEmailStatus({ status: 'error', message: '올바른 형식으로 입력해주세요.' });
      isValidForm = false;
    }

    return isValidForm;
  };

  const handleConfirm = async () => {
    const isValidForm = validateForm();

    if (isValidForm) {
      try {
        setIsLoading(true);

        const params: TypeOwnerSupplierManager = {
          ownerSupplierManagerName: managerAddForm.name,
          ownerSupplierManagerPosition: managerAddForm.position.trim(),
          ownerSupplierManagerTel: managerAddForm.tel,
          ownerSupplierManagerEmail: managerAddForm.email,
        };

        await onConfirm(params);

        resetForm();
      } catch (error) {
        toastService.addToast({
          type: 'error',
          message: (error as TypeErrorResponseData['error'])?.message || TOAST_MESSAGE.DEFAULT_ERROR,
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <Modal open={open} title="담당자 추가" confirmText="추가" onCancel={handleCancel} onConfirm={handleConfirm}>
      <div className={styles['modal-contents-container']}>
        <FormField
          label="담당자명"
          required
          contents={
            <Input
              type="text"
              size="small"
              placeholder="입력"
              status={nameStatus.status}
              validationMessage={nameStatus.message}
              value={managerAddForm.name}
              onFocus={handleFocusName}
              onChange={handleChangeName}
            />
          }
          labelClassName={styles['input-row-label']}
        />
        <FormField
          label="직위"
          contents={
            <Input
              type="text"
              size="small"
              placeholder="입력"
              value={managerAddForm.position}
              onChange={handleChangePosition}
            />
          }
          labelClassName={styles['input-row-label']}
        />
        <FormField
          label="휴대폰번호"
          required
          contents={
            <Input
              type="text"
              size="small"
              placeholder="- 포함 입력"
              status={telStatus.status}
              validationMessage={telStatus.message}
              value={managerAddForm.tel}
              onFocus={handleFocusTel}
              onChange={handleChangeTel}
            />
          }
          labelClassName={styles['input-row-label']}
        />
        <FormField
          label="이메일"
          contents={
            <Input
              type="text"
              size="small"
              placeholder="example@kls.com"
              status={emailStatus.status}
              validationMessage={emailStatus.message}
              value={managerAddForm.email}
              onFocus={handleFocusEmail}
              onChange={handleChangeEmail}
            />
          }
          labelClassName={styles['input-row-label']}
        />
      </div>
    </Modal>
  );
};

export default VenderSupplierManagerAddModal;
